import { useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Badge } from '@kollez/design-system';
import { useState } from 'react';

/**
 * Kanban board with @dnd-kit. Columns are stable strings from the KANBAN_STAGES enum; cards are
 * light objects `{ id, name, branch, match, stage }`. `onMove(id, from, to)` is the callback the
 * DriveDetail wires to `useMoveApplication()` — this component is presentational + drag mechanics.
 */

export interface KanbanCard {
  id: string;
  name: string;
  sub?: string;
  match?: number;
  stage: string;
  flag?: string;
}

export interface KanbanBoardProps {
  columns: Array<{ key: string; label: string }>;
  cards: KanbanCard[];
  onMove: (cardId: string, fromStage: string, toStage: string) => void;
}

export function KanbanBoard({ columns, cards, onMove }: KanbanBoardProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const grouped = useMemo(() => {
    const bucket: Record<string, KanbanCard[]> = {};
    for (const col of columns) bucket[col.key] = [];
    for (const c of cards) (bucket[c.stage] ?? bucket['applied'])?.push(c);
    return bucket;
  }, [columns, cards]);

  const active = cards.find((c) => c.id === activeId) ?? null;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(e) => setActiveId(String(e.active.id))}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={(e: DragEndEvent) => {
        setActiveId(null);
        const from = String(e.active.data.current?.stage ?? '');
        const to = String(e.over?.id ?? '');
        if (!to || from === to) return;
        onMove(String(e.active.id), from, to);
      }}
    >
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(200px, 1fr))`, overflowX: 'auto' }}
      >
        {columns.map((col) => (
          <Column key={col.key} id={col.key} label={col.label} cards={grouped[col.key] ?? []} />
        ))}
      </div>
      <DragOverlay>{active ? <Card card={active} dragging /> : null}</DragOverlay>
    </DndContext>
  );
}

function Column({ id, label, cards }: { id: string; label: string; cards: KanbanCard[] }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`card p-3 min-h-40 transition ${isOver ? 'ring-2 ring-ink-accent' : ''}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-sm">{label}</span>
        <Badge tone="outline">{cards.length}</Badge>
      </div>
      <div className="grid gap-2">
        {cards.map((c) => <Card key={c.id} card={c} />)}
        {cards.length === 0 && (
          <div className="text-xs text-ink-tertiary text-center py-2 border border-dashed border-line-tertiary rounded">
            Drop here
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ card, dragging }: { card: KanbanCard; dragging?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: card.id,
    data: { stage: card.stage },
    disabled: dragging,
  });
  const style: React.CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1 }
    : {};
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`border border-line-tertiary rounded p-2 bg-primary cursor-grab ${dragging ? 'shadow-md' : ''}`}
    >
      <div className="text-sm font-medium truncate">{card.name}</div>
      <div className="text-xs text-ink-tertiary flex justify-between">
        <span className="truncate">{card.sub ?? ''}</span>
        {typeof card.match === 'number' && (
          <span className={card.match >= 85 ? 'text-ink-success' : ''}>{card.match}%</span>
        )}
      </div>
    </div>
  );
}
